import React, { ReactNode, useState, useEffect } from 'react';
import styled from 'styled-components';
import Navbar from './Navbar';
import Footer from './Footer';
import AudioPlayer from './AudioPlayer';
import CartSidebar from './CartSidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '@context/AudioContext';
import { useCart } from '@context/CartContext';
import BackgroundVisuals from '@components/effects/BackgroundVisuals';

interface LayoutProps {
  children: ReactNode;
}

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
`;

const MainContent = styled(motion.main)`
  flex: 1;
  padding-top: 80px; // Height of the navbar
  position: relative;
  z-index: 1;
`;

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.99]
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: [0.6, 0.05, 0.01, 0.99]
    }
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isCartOpen, toggleCart, closeCart } = useCart();
  const { currentTrack } = useAudio();
  const [visualIntensity, setVisualIntensity] = useState(0.5);

  // Effect to increase visual intensity when music is playing
  useEffect(() => {
    if (currentTrack) {
      setVisualIntensity(0.8); // Higher intensity when music is playing
    } else {
      setVisualIntensity(0.5); // Default intensity
    }
  }, [currentTrack]);

  return (
    <LayoutContainer>
      {/* Psychedelic background visuals */}
      <BackgroundVisuals intensity={visualIntensity} />

      {/* Navbar */}
      <Navbar toggleCart={toggleCart} />

      {/* Main content with page transition */}
      {/* No page-level fade: initial="hidden" painted EVERY page at opacity 0
          until JS ran, flashing on first load and on each navigation. */}
      <MainContent
        variants={contentVariants}
        initial={false}
        animate="visible"
        exit="exit"
      >
        {children}
      </MainContent>

      {/* Footer */}
      <Footer />

      {/* Fixed components */}
      <AudioPlayer />

      {/* License inquiry panel with animation */}
      <AnimatePresence>
        {isCartOpen && (
          <CartSidebar
            isOpen={isCartOpen}
            onClose={closeCart}
          />
        )}
      </AnimatePresence>
    </LayoutContainer>
  );
};

export default Layout;